import { Dispatch, SetStateAction } from 'react';
import { useOpportunityReviewBoardGroupEventState } from './utils/OpportunityReviewBoardGroupContext';
import {
  GROUP_PGS_LIMIT_MESSAGE,
  GroupContext,
  OPPORTUNITY_REVIEW_BOARD_TABLE,
  PreferenceResponseStatus,
  PRODUCT_GROUP_MAX_SELECTION,
  totalNumberSelectedForAddingItems,
  totalNumberSelectedForItems,
} from '@utils/userPreferencesUtils/Types';
import { actions } from '@ecosystem/newron-design-system';
import { useAnalytics } from '@ecosystem/newron-client-lib';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { useSelector } from 'react-redux';
import { useOpportunityReviewGroupState } from '@utils/hook/userPreferences/UseGroupState';
import { PageName, PageType } from '@utils/pageConfig/Types';
import {
  groupElementClickedTrackingFunction,
  OPPORTUNITY_REVIEW_TABLE_ELEMENT_CLICKED_TITLE,
  TableElementClickedParam,
} from '@utils/page-analytics/model/GroupElementClickTracking';
import ActionBar from '@components/action-bar/ActionBar';

const { Button } = actions;

interface Props {
  totalProductGroups: number;
  context: GroupContext;
  isEditable: boolean;
  setIsEditable: Dispatch<SetStateAction<boolean>>;
}

export const OpportunityReviewBoardActionBar: React.FC<Props> = ({
  totalProductGroups,
  context,
  isEditable,
  setIsEditable,
}) => {
  const {
    selectedSegments,
    setIsCreateModalGroupPopUp,
    isEditGroupMode,
    setIsEditGroupMode,
    clearSelectedSegments,
    currentSelectedFeature,
  } = useOpportunityReviewBoardGroupEventState();

  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const { updateOpportunityReviewGroup } = useOpportunityReviewGroupState(context);

  const noPgsSelected: boolean = selectedSegments.length === 0;
  const atLeastOnePgSelected: boolean = selectedSegments.length >= 1;
  const isGroupPgsExceeded: boolean = selectedSegments.length > PRODUCT_GROUP_MAX_SELECTION;

  const totalSelectedProductGroupsInEditMode =
    selectedSegments.length + isEditGroupMode.groupDetails.groupElement.length;

  const isGroupPgsExceededInEditMode: boolean = totalSelectedProductGroupsInEditMode > PRODUCT_GROUP_MAX_SELECTION;

  const handleCancelEditMode = () => {
    setIsEditGroupMode({
      groupDetails: { groupName: '', groupElement: [], sortOrder: undefined },
      isEditMode: false,
    });
  };

  if (isEditable) {
    setIsEditGroupMode({
      groupDetails: { groupName: '', groupElement: [], sortOrder: undefined },
      isEditMode: false,
    });
    setIsEditable(false);
    clearSelectedSegments();
  }
  const constructDataBody = (isCateogry: boolean, updatedSegment: string[]) => {
    if (isCateogry) {
      return {
        productGroup: updatedSegment,
        showPerformanceWithin: currentSelectedFeature.feature,
        sortOrder: isEditGroupMode.groupDetails.sortOrder!,
      };
    }
    return {
      value: updatedSegment,
      showPerformanceWithin: currentSelectedFeature.feature,
      sortOrder: isEditGroupMode.groupDetails.sortOrder!,
    };
  };

  const constructUpdateOpportunityReviewGroupPreference = (updatedSegment: string[]) => {
    updateOpportunityReviewGroup.mutate(
      {
        key: isEditGroupMode.groupDetails.groupName,
        data: constructDataBody(context.isCategory, updatedSegment),
      },
      {
        onSuccess: (response) => {
          if (response.status === PreferenceResponseStatus.SUCCESS) {
            clearSelectedSegments();
            handleCancelEditMode();
          }
        },
      },
    );
  };

  const handleUpdateGroups = () => {
    const updatedSegment: string[] = [...isEditGroupMode.groupDetails.groupElement, ...selectedSegments];

    //Amplitude Tracking when Add product group(s) button is click
    const trackingParam: TableElementClickedParam = {
      eventTitle: OPPORTUNITY_REVIEW_TABLE_ELEMENT_CLICKED_TITLE,
      elementName: `Add ${currentSelectedFeature.feature} group`,
      elementType: `Table ${currentSelectedFeature.feature} group`,
      track: track,
      optionChosen: selectedSegments.flatMap((selectedPg) => selectedPg).join(', '),
      pageName: PageName.OPPORTUNITY_REVIEW,
      pageType: PageType.EXPLORE,
      sideFilters: sideFiltersState.appliedFilters,
      access: pagesConfig.access,
      tableName: OPPORTUNITY_REVIEW_BOARD_TABLE,
    };

    groupElementClickedTrackingFunction(trackingParam);

    constructUpdateOpportunityReviewGroupPreference(updatedSegment);
  };

  if (isEditGroupMode.isEditMode) {
    return (
      <ActionBar
        isHidden={!isEditGroupMode.isEditMode}
        leftMessage={totalNumberSelectedForAddingItems(
          isEditGroupMode.groupDetails.groupName,
          totalSelectedProductGroupsInEditMode,
          PRODUCT_GROUP_MAX_SELECTION,
        )}
        centerMessage={isGroupPgsExceededInEditMode ? GROUP_PGS_LIMIT_MESSAGE(currentSelectedFeature.feature) : ''}
      >
        {atLeastOnePgSelected && (
          <Button
            testId="cl-action-bar-add-to-group"
            disabled={isGroupPgsExceededInEditMode}
            onClick={handleUpdateGroups}
          >
            Add to group
          </Button>
        )}
        <Button testId="cl-action-bar-cancel" mode="secondary" onClick={handleCancelEditMode}>
          Cancel
        </Button>
      </ActionBar>
    );
  }

  return (
    <ActionBar
      leftMessage={totalNumberSelectedForItems(
        selectedSegments.length,
        totalProductGroups,
        currentSelectedFeature.feature,
      )}
      centerMessage={isGroupPgsExceeded ? GROUP_PGS_LIMIT_MESSAGE(currentSelectedFeature.feature) : ''}
      rightMessage={noPgsSelected ? GROUP_PGS_LIMIT_MESSAGE(currentSelectedFeature.feature) : ''}
    >
      {atLeastOnePgSelected && (
        <Button
          testId="cl-action-bar-group"
          disabled={isGroupPgsExceeded}
          onClick={() => setIsCreateModalGroupPopUp(true)}
        >
          Group
        </Button>
      )}
    </ActionBar>
  );
};
